<template>
  <modal :show.sync="visible" @update:show="handleModalClose" class="dark-modal">
    <div class="modal-box">
      <div class="modal-box-header">
        <h2 class="modal-title">{{ $t('users.filter.title') }}</h2>
      </div>

      <div class="data-input-filter-content">
        <!-- Status Section -->
        <div class="field data-input-filter-section">
          <label class="label data-input-filter-section-label">{{ $t('status.status') }}</label>
          <div class="field is-grouped is-grouped-multiline data-input-filter-options">
            <div class="control" v-for="status in availableStatuses" :key="status.value">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="status.value"
                  v-model="localFilters.enabled"
                  @change="updateResultCount"
                >
                {{ status.label }}
              </label>
            </div>
          </div>
        </div>

        <!-- SSL Section -->
        <div class="field data-input-filter-section">
          <label class="label data-input-filter-section-label">SSL</label>
          <div class="field is-grouped is-grouped-multiline data-input-filter-options">
            <div class="control" v-for="ssl in availableSSLOptions" :key="ssl.value">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="ssl.value"
                  v-model="localFilters.ssl"
                  @change="updateResultCount"
                >
                {{ ssl.label }}
              </label>
            </div>
          </div>
        </div>

        <!-- Protocol Section -->
        <div class="field data-input-filter-section">
          <label class="label data-input-filter-section-label">{{ $t('protocol') }}</label>
          <div class="field is-grouped is-grouped-multiline data-input-filter-options">
            <div class="control" v-for="protocol in availableProtocols" :key="protocol.value">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :value="protocol.value"
                  v-model="localFilters.protocol"
                  @change="updateResultCount"
                >
                {{ protocol.label }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          type="button"
          class="button cancel-button"
          @click="cancelFilters"
        >
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="button cancel-button"
          @click="clearAllFilters"
        >
          {{ $t('users.filter.clearAll') }}
        </button>
        <button
          type="button"
          class="button is-primary"
          @click="confirmFilters"
          :disabled="isLoading"
          :class="{ 'is-loading': isLoading }"
        >
          {{ $t('users.filter.confirm') }}
          <span v-if="resultCount !== null" class="tag is-light ml-2">
            {{ resultCount }}
          </span>
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { Modal } from '@cyradar/ui'
import { debounce } from '@/utils'

export default {
  name: 'DataInputFilter',
  components: {
    Modal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({
        enabled: [],
        ssl: [],
        protocol: []
      })
    }
  },
  data() {
    return {
      visible: false,
      localFilters: {
        enabled: [],
        ssl: [],
        protocol: []
      },
      resultCount: null,
      isLoading: false
    }
  },
  computed: {
    /**
     * Available enable status options for filtering
     */
    availableStatuses() {
      return [
        { value: 'true', label: this.$t('active') },
        { value: 'false', label: this.$t('inactive') }
      ]
    },
    /**
     * Available SSL options for filtering
     */
    availableSSLOptions() {
      return [
        { value: 'true', label: this.$t('enabled') },
        { value: 'false', label: this.$t('disabled') }
      ]
    },
    /**
     * Available protocol options for filtering
     */
    availableProtocols() {
      return [
        { value: 'http', label: 'HTTP' },
        { value: 'tcp', label: 'TCP' },
        { value: 'udp', label: 'UDP' }
      ]
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        this.visible = newVal
        if (newVal) {
          this.initializeFilters()
        }
      }
    },
    filters: {
      deep: true,
      immediate: true,
      handler(newFilters) {
        if (newFilters) {
          this.localFilters = {
            enabled: [...(newFilters.enabled || [])],
            ssl: [...(newFilters.ssl || [])],
            protocol: [...(newFilters.protocol || [])]
          }
        }
      }
    }
  },
  created() {
    // Debounced function for updating result count
    this.debouncedUpdateResultCount = debounce(this.fetchResultCount, 500)
  },
  methods: {
    /**
     * Initialize filters when modal opens
     */
    initializeFilters() {
      this.localFilters = {
        enabled: [...(this.filters.enabled || [])],
        ssl: [...(this.filters.ssl || [])],
        protocol: [...(this.filters.protocol || [])]
      }
      this.resultCount = null
    },
    /**
     * Handle modal close event
     */
    handleModalClose(show) {
      this.visible = show
      this.$emit('update:show', show)
      if (!show) {
        this.resultCount = null
      }
    },
    /**
     * Update result count when filters change
     */
    updateResultCount() {
      if (this.hasActiveFilters()) {
        this.debouncedUpdateResultCount()
      } else {
        this.resultCount = null
      }
    },
    /**
     * Fetch result count based on current filters
     */
    async fetchResultCount() {
      if (!this.hasActiveFilters()) {
        this.resultCount = null
        return
      }

      this.isLoading = true
      try {
        const params = this.buildFilterParams()
        params.limit = 1 // We only need the count, not the actual data

        const response = await this.$http.get('/api/v1/data-inputs', { params })
        if (response && response.data && response.data.data) {
          this.resultCount = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching result count:', error)
        this.resultCount = null
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Build filter parameters for API request
     */
    buildFilterParams() {
      const params = {}

      // Convert filters to API format - send as comma-separated strings
      if (this.localFilters.enabled.length > 0) {
        params.enabled = this.localFilters.enabled.join(',')
      }
      if (this.localFilters.ssl.length > 0) {
        params.ssl = this.localFilters.ssl.join(',')
      }
      if (this.localFilters.protocol.length > 0) {
        params.protocol = this.localFilters.protocol.join(',')
      }

      return params
    },
    /**
     * Check if any filters are currently active
     */
    hasActiveFilters() {
      return this.localFilters.enabled.length > 0 || 
             this.localFilters.ssl.length > 0 || 
             this.localFilters.protocol.length > 0
    },
    /**
     * Confirm and apply filters
     */
    confirmFilters() {
      this.$emit('apply-filters', {
        enabled: [...this.localFilters.enabled],
        ssl: [...this.localFilters.ssl],
        protocol: [...this.localFilters.protocol],
        resultCount: this.resultCount // Pass the result count to parent
      })
      // Close modal after a slight delay to ensure event processing
      this.$nextTick(() => {
        this.handleModalClose(false)
      })
    },
    /**
     * Clear all filters
     */
    clearAllFilters() {
      this.localFilters = {
        enabled: [],
        ssl: [],
        protocol: []
      }
      this.resultCount = null
    },
    /**
     * Cancel filter changes
     */
    cancelFilters() {
      this.handleModalClose(false)
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/dark-modal.scss';

.data-input-filter-content {
  padding: 1rem 0;
}

.data-input-filter-section {
  margin-bottom: 1.5rem;
}

.data-input-filter-section-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: $modal-text;
}

.data-input-filter-options {
  margin-top: 0.5rem;
}

.data-input-filter-options .control {
  margin-right: 1rem;
  margin-bottom: 0.5rem;
}

.data-input-filter-options .checkbox {
  font-weight: normal;
  color: $modal-text;
}

.ml-2 {
  margin-left: 0.5rem;
}
</style>
